import React, { useState, useEffect } from 'react';
import { DataProviderInstance } from '../../api/DataProvider';
import styles from './MessagesTemplates.module.css';
import Loading from '../../components/loading/Loading.js';
import {
    Typography,
    Snackbar,
    Alert
} from '@mui/material';
import {
    MessageOutlined as MessageIcon,
    EventAvailable as EventAvailableIcon,
    NotificationsActive as NotificationsActiveIcon,
    Schedule as ScheduleIcon,
    Star as StarIcon,
    RateReview as RateReviewIcon,
    InfoOutlined as InfoOutlinedIcon,
    Cancel as CancelIcon,
    LocalHospital as LocalHospitalIcon
} from '@mui/icons-material/';
import EditMessageTemplate from '../../popup/EditMessageTemplate/EditMessageTemplate';
import TemplateCard from './components/TemplateCard';
import TemplatePreviewModal from './components/TemplatePreviewModal';
import { useWhatsAppSender } from '../../contexts/WhatsAppSenderContext';
import WhatsAppOriginBanner from '../../components/whatsapp/WhatsAppOriginBanner';


const MessagesTemplates = () => {
    const { GetMessageTemplates, UpdateMessageTemplate } = DataProviderInstance();
    const { capabilities } = useWhatsAppSender();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [isEditPopupVisible, setIsEditPopupVisible] = useState(false);
    const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
    const [previewTemplate, setPreviewTemplate] = useState(null);
    
    // Estado para notificações
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        loadMessageTemplates(() => setLoading(false));
    }, []);

    const loadMessageTemplates = (callback) => {
        GetMessageTemplates((response) => {
            if (response && response.length > 0) {
                // A API já retorna todos os templates necessários (incluindo fallbacks)
                const processedResponse = response.map(template => ({
                    ...template,
                    isFallback: template.IsDefault || false
                }));
                
                setTemplates(processedResponse);
            } else {
                setTemplates([]);
            }
            if(callback) callback();
        });
    };

    const getTemplateCategory = (key) => {
        const categories = {
            // 1. Agendamentos
            'AppointmentCreatedFullMessage': 'agendamentos',
            'AppointmentCreatedShortMessage': 'agendamentos',
            'AppointmentRescheduledConfirmedMessage': 'agendamentos',
            'AppointmentReservationMessage': 'agendamentos',
            'AppointmentReservationReminder': 'agendamentos',
            'AppointmentReservationReleased': 'agendamentos',

            // 2. Confirmações
            'AppointmentAskForConfirmation1': 'confirmacoes',
            'AppointmentAskForConfirmation2': 'confirmacoes',
            'AppointmentAskForConfirmation3': 'confirmacoes',
            
            // 3. Cancelamentos
            'AppointmentCanceledMessage': 'cancelamentos',
            'AppointmentCancellationDueLackOfReturn': 'cancelamentos',
            
            // 4. Lembretes e Follow-ups
            'Appointment7DaysReminder': 'lembretes',
            'CanceledAppointmentFollowUp': 'lembretes',
            'FirstAppointmentFollowup': 'lembretes',
            
            // 5. Especiais
            'HappyBirthday': 'especiais',
            'SuggestDate': 'especiais',
            'SuggestDateFromWaitingList': 'especiais',
            
            // 6. Feedback
            'NPSFeedbackRequest': 'feedback',
            'GoogleReviewRequest': 'feedback',
            
            // 7. Atendimento
            'AppointmentAssessmentTemplate': 'atendimento',
            'AppointmentEvolutionTemplate': 'atendimento'
        };
        
        return categories[key] || 'outros';
    };

    const getTemplateDisplayName = (key) => {
        const displayNames = {
            // Agendamentos
            'AppointmentCreatedFullMessage': 'Confirmação de Agendamento (Completa)',
            'AppointmentCreatedShortMessage': 'Confirmação de Agendamento (Resumida)',
            'AppointmentRescheduledConfirmedMessage': 'Reagendamento Confirmado',
            'AppointmentReservationMessage': 'Reserva de Horário',
            'AppointmentReservationReminder': 'Lembrete de Cadastro da Reserva',
            'AppointmentReservationReleased': 'Reserva Liberada',

            // Confirmações
            'AppointmentAskForConfirmation1': 'Primeira Confirmação',
            'AppointmentAskForConfirmation2': 'Segunda Confirmação',
            'AppointmentAskForConfirmation3': 'Terceira Confirmação',
            
            // Cancelamentos
            'AppointmentCanceledMessage': 'Cancelamento de Agendamento',
            'AppointmentCancellationDueLackOfReturn': 'Cancelamento por Falta de Retorno',
            
            // Lembretes e Follow-ups
            'Appointment7DaysReminder': 'Lembrete 7 Dias',
            'CanceledAppointmentFollowUp': 'Follow-up Agendamento Cancelado',
            'FirstAppointmentFollowup': 'Follow-up Primeiro Agendamento',
            
            // Especiais
            'HappyBirthday': 'Feliz Aniversário',
            'SuggestDate': 'Sugestão de Data',
            'SuggestDateFromWaitingList': 'Sugestão da Lista de Espera',
            
            // Feedback
            'NPSFeedbackRequest': 'Solicitação de Feedback NPS',
            'GoogleReviewRequest': 'Solicitação de Avaliação Google',
            
            // Atendimento
            'AppointmentAssessmentTemplate': 'Avaliação inicial',
            'AppointmentEvolutionTemplate': 'Evolução'
        };
        
        return displayNames[key] || key;
    };

    const getCategoryInfo = (category) => {
        const categoryInfo = {
            'agendamentos': {
                name: 'Agendamentos',
                icon: <EventAvailableIcon />,
                color: 'var(--color-blue-main)'
            },
            'confirmacoes': {
                name: 'Confirmações',
                icon: <NotificationsActiveIcon />,
                color: 'var(--color-warning)'
            },
            'cancelamentos': {
                name: 'Cancelamentos',
                icon: <CancelIcon />,
                color: '#dc3545'
            },
            'lembretes': {
                name: 'Lembretes e Follow-ups',
                icon: <ScheduleIcon />,
                color: 'var(--color-success)'
            },
            'especiais': {
                name: 'Especiais',
                icon: <StarIcon />,
                color: 'var(--color-secondary)'
            },
            'feedback': {
                name: 'Feedback',
                icon: <RateReviewIcon />,
                color: 'var(--color-info)'
            },
            'atendimento': {
                name: 'Atendimento',
                icon: <LocalHospitalIcon />,
                color: 'var(--color-primary)'
            }
        };
        
        return categoryInfo[category] || {
            name: 'Outros',
            icon: <MessageIcon />,
            color: 'var(--color-text-medium)'
        };
    };

    const groupTemplatesByCategory = (templates) => {
        const grouped = {};
        
        templates.forEach(template => {
            const category = getTemplateCategory(template.TemplateKey);
            if (!grouped[category]) {
                grouped[category] = [];
            }
            grouped[category].push(template);
        });
        
        return grouped;
    };

    const handleEditTemplate = (template) => {
        // Modo plataforma (número da Gabriela): templates ficam em modo
        // leitura, itens 1-8 sempre usam o texto padrão — edição só com
        // número próprio conectado
        if (!capabilities.template_edit) {
            setNotification({
                open: true,
                message: 'Edição de templates disponível apenas com número WhatsApp próprio.',
                severity: 'info'
            });
            return;
        }
        setSelectedTemplate(template);
        setIsEditPopupVisible(true);
    };

    const handlePreviewTemplate = (template) => {
        setPreviewTemplate(template);
        setIsPreviewModalVisible(true);
    };

    const handleCopyTemplate = (template) => {
        setNotification({
            open: true,
            message: 'Template copiado para a área de transferência!',
            severity: 'success'
        });
    };

    const handleClose = () => {
        setIsEditPopupVisible(false);
        setSelectedTemplate(null);
    };

    const handleUpdate = () => {
        setIsEditPopupVisible(false);
        setTemplates([]);
        loadMessageTemplates();
        setNotification({
            open: true,
            message: 'Template atualizado com sucesso!',
            severity: 'success'
        });
    };

    if (loading) {
        return <Loading />;
    }

    const groupedTemplates = groupTemplatesByCategory(templates);
    const hasResults = templates.length > 0;

    return (
        <div className={styles.container}>
            <WhatsAppOriginBanner context="template" />

            {/* Modais */}
            {isEditPopupVisible && (
                <EditMessageTemplate 
                    template={selectedTemplate}
                    displayName={getTemplateDisplayName(selectedTemplate.TemplateKey)}
                    onUpdate={handleUpdate}
                    onHide={handleClose}
                />
            )}
            
            <TemplatePreviewModal
                open={isPreviewModalVisible}
                onClose={() => setIsPreviewModalVisible(false)}
                template={previewTemplate}
                onEdit={handleEditTemplate}
            />


            {/* Conteúdo */}
            {hasResults ? (
                Object.entries(groupedTemplates).map(([category, categoryTemplates]) => {
                    const categoryInfo = getCategoryInfo(category);
                    
                    return (
                        <div key={category} className={`${styles.categorySection} ${styles[category]}`}>
                            <div className={styles.categoryHeader}>
                                <div 
                                    className={styles.categoryIcon}
                                    style={{ backgroundColor: categoryInfo.color }}
                                >
                                    {categoryInfo.icon}
                                </div>
                                <h2 className={styles.categoryTitle}>
                                    {categoryInfo.name}
                                </h2>
                                <span className={styles.categoryCount}>
                                    {categoryTemplates.length}
                                </span>
                            </div>
                            
                            <div className={styles.templatesGrid}>
                                {categoryTemplates.map((template) => (
                                    <TemplateCard
                                        key={template.TemplateKey}
                                        template={template}
                                        category={category}
                                        onEdit={handleEditTemplate}
                                        onPreview={handlePreviewTemplate}
                                        onCopy={handleCopyTemplate}
                                    />
                                ))}
                            </div>
                        </div>
                    );
                })
            ) : (
                <div className={styles.emptyState}>
                    <InfoOutlinedIcon className={styles.emptyStateIcon} />
                    <Typography variant="h6" className={styles.emptyStateText}>
                        Nenhum template encontrado
                    </Typography>
                    <Typography variant="body2" className={styles.emptyStateSubtext}>
                        Não há templates de mensagem disponíveis
                    </Typography>
                </div>
            )}

            {/* Notificações */}
            <Snackbar
                open={notification.open}
                autoHideDuration={3000}
                onClose={() => setNotification({ ...notification, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert 
                    onClose={() => setNotification({ ...notification, open: false })} 
                    severity={notification.severity}
                    variant="filled"
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default MessagesTemplates;